import { useVerificationsOverviewController } from "shared/queries/useVerificationsOverviewController";
import {
  IncomeStatus,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { has, formatToCurrency } from "shared/utils";
import { Money } from "types/landlord/reports";
import { AuditSummaryFlag } from "views/applications-id/components/ApplicantsTable/components/AuditSummaryFlag";
import { Cell } from "views/applications-id/components/ApplicantsTable/components/Cell";
import { ReportStatusBadge } from "views/applications-id/components/ApplicantsTable/components/ReportStatusBadge";
import {
  ApplicantOrInvitee,
  Verification
} from "views/applications-id/components/ApplicantsTable/types";
import { useAuthContext } from "providers/Auth";
import { Applicant } from "types/application";
import { EmptyCellIcon } from "./components/EmptyCellIcon";
import { CombinedIncome } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";

const IN_PROGRESS_STATUSES = [
  VerificationStatus.Started,
  VerificationStatus.Submitted
];

type CombinedIncomeVerification = Verification & {
  incomes?: CombinedIncome[];
};

const getUIStatus = ({
  verification,
  hasWarnings
}: {
  verification: CombinedIncomeVerification;
  hasWarnings: boolean;
}): VerificationStatus => {
  if (hasWarnings) return VerificationStatus.FinishedWithWarnings;

  if (IN_PROGRESS_STATUSES.includes(verification.status))
    return VerificationStatus.Started;

  if (
    verification.incomes?.some(
      (income) => income.status === IncomeStatus.Processing
    )
  )
    return VerificationStatus.Processing;

  return verification.status;
};

const DataPoint = ({
  val,
  applicant,
  hasAuditFlagEnabled
}: {
  val: string | number | undefined | null | Money;
  applicant: ApplicantOrInvitee;
  hasAuditFlagEnabled: boolean;
}) => {
  if (has(val) && typeof val === "object" && "cents" in val)
    return <span>{formatToCurrency(val)}</span>;

  if (hasAuditFlagEnabled)
    return (
      <AuditSummaryFlag
        audit_summary_flags={
          "audit_summary_flags" in applicant
            ? applicant.audit_summary_flags
            : undefined
        }
      />
    );

  return <EmptyCellIcon />;
};

export const CombinedIncomeReportSection = ({
  applicant,
  verification,
  hasAuditFlagEnabled
}: {
  applicant: ApplicantOrInvitee;
  hasAuditFlagEnabled: boolean;
  verification: Verification;
}) => {
  const { permissions } = useAuthContext();

  const { verificationsOverviewMeta } = useVerificationsOverviewController({
    applicationId: Number(applicant.application_id),
    applicantId: Number(applicant.id)
  });

  const status = getUIStatus({
    verification: verification,
    hasWarnings:
      verificationsOverviewMeta.data?.[VerificationNames.CombinedIncome]
        ?.has_warnings ?? false
  });

  return (
    <>
      {/* Empty cell for all rows */}
      <Cell className="bg-background-900" />
      <Cell
        isDataCell
        className="relative"
        showRestrictedFlag={!permissions.canAccessReports.incomeReport}
        isPermitted={permissions.canAccessReports.incomeReport}
      >
        {/* Gross income (Verified) */}
        <DataPoint
          applicant={applicant}
          val={verification.summary?.verified_gross_monthly_total}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
        />
        <ReportStatusBadge
          verification={verification as Verification}
          applicant={applicant as Applicant}
          overrideStatus={status}
        />
      </Cell>
      {/* Net income (Verified) */}
      <Cell isDataCell isPermitted={permissions.canAccessReports.incomeReport}>
        <DataPoint
          applicant={applicant}
          val={verification.summary?.verified_net_monthly_total}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
        />
      </Cell>
      {/* Gross income (Self-reported) */}
      <Cell isDataCell isPermitted={permissions.canAccessReports.incomeReport}>
        <DataPoint
          applicant={applicant}
          val={verification.summary?.self_reported_gross_monthly_total}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
        />
      </Cell>
    </>
  );
};
